import React, { createContext, useContext, useState, useCallback, useEffect, useRef, ReactNode } from "react";
import ICart from "../models/ICart";
import ICart_Item from "../models/ICart_Item";
import CartService from "../services/CartService";
import IUserUI from "../models/IUserUI";
import ISupplierModel from "../models/ISupplierModel";
import IVendorModel from "../models/IVendorModel";
import { IInactiveCartSummary } from "../models/DTO/IOnGetInactiveCartsDTO";

const INACTIVE_CART_POLL_INTERVAL_MS = 60_000;

export interface ICartContext {
    cart: ICart | null;
    isLoading: boolean;
    refreshCart: () => Promise<void>;
    updateCartItem: (updatedItem: ICart_Item) => void;
    inactiveCarts: IInactiveCartSummary[];
    inactiveCartCount: number;
    refreshInactiveCarts: () => Promise<void>;
}

const CartContext = createContext<ICartContext | undefined>(undefined);

export interface CartProviderProps {
    userModel: IUserUI;
    supplierModel: ISupplierModel;
    vendorModel?: IVendorModel;
    children: ReactNode;
}

export const CartProvider: React.FC<CartProviderProps> = ({ userModel, supplierModel, vendorModel, children }) => {
    const [cart, setCart] = useState<ICart | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [inactiveCarts, setInactiveCarts] = useState<IInactiveCartSummary[]>([]);

    const refreshCart = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await CartService.getCart(userModel, supplierModel, vendorModel);
            setCart(response.cart);
        } catch (error) {
            console.error("Failed to get cart:", error);
        } finally {
            setIsLoading(false);
        }
    }, [userModel, supplierModel, vendorModel]);

    const updateCartItem = useCallback((updatedItem: ICart_Item) => {
        setCart((prev) => {
            if (!prev || !prev.Cart_Items) return prev;
            const updatedItems = prev.Cart_Items.map((ci) => (ci.guid === updatedItem.guid ? { ...ci, qty: updatedItem.qty } : ci));
            return { ...prev, Cart_Items: updatedItems };
        });
    }, []);

    const refreshInactiveCarts = useCallback(async () => {
        if (!userModel?.guid || !userModel?.token || !supplierModel?.guid) {
            return;
        }

        try {
            const response = await CartService.getInactiveCarts(
                userModel,
                supplierModel,
                userModel.isSupplierUser ? undefined : vendorModel
            );
            setInactiveCarts(response.carts ?? []);
        } catch (error) {
            console.warn("Failed to fetch inactive carts", error);
        }
    }, [userModel, supplierModel, vendorModel]);

    const refreshInactiveCartsRef = useRef(refreshInactiveCarts);
    refreshInactiveCartsRef.current = refreshInactiveCarts;

    useEffect(() => {
        refreshInactiveCartsRef.current();

        const intervalId = setInterval(() => {
            refreshInactiveCartsRef.current();
        }, INACTIVE_CART_POLL_INTERVAL_MS);

        return () => clearInterval(intervalId);
    }, [userModel?.guid, supplierModel?.guid, vendorModel?.guid]);

    return React.createElement(
        CartContext.Provider,
        {
            value: {
                cart,
                isLoading,
                refreshCart,
                updateCartItem,
                inactiveCarts,
                inactiveCartCount: inactiveCarts.length,
                refreshInactiveCarts,
            },
        },
        children
    );
};

export const useCart = (): ICartContext => {
    const context = useContext(CartContext);
    if (context === undefined) {
        throw new Error("useCart must be used within a CartProvider");
    }
    return context;
};

export default CartContext;
